import { ServiceOpts, useClient } from "./client";
import { HttpExceptionModel, HttpValidationError } from "./gen";
import type { Client } from "./gen/client/types.gen";
import * as services from "./gen/sdk.gen";

// NOT exporting schemas here due to cirular dependency between types and utils
// https://github.com/hey-api/openapi-ts/issues/2238
// export * as schemas from "./gen/schemas.gen";
export type * as types from "./gen/types.gen";

type ServiceFunction<T> = T extends (options: infer O) => unknown
    ? O extends { client?: Client }
        ? (options: Omit<O, "client">) => ReturnType<T>
        : T
    : T;

type Services = Omit<
    {
        [K in keyof typeof services]: ServiceFunction<(typeof services)[K]>;
    },
    "client"
>;

/**
 * Use the services with our own client.
 */
const useServices = ({
    opts,
    requires,
    overrides,
}: {
    opts?: {
        throwOnError: boolean;
    };
} & ServiceOpts): Services => {
    const client = useClient({ requires, overrides });

    // Set default values for opts.
    if (!opts) {
        opts = { throwOnError: true };
    }
    if (opts?.throwOnError === undefined) {
        opts.throwOnError = true;
    }

    return Object.fromEntries(
        Object.entries(services as Services).map(([key, service]) => [
            key,

            (options: Record<string, unknown>) =>
                service({ client, ...opts, ...options }),
        ])
    ) as Services;
};

/**
 * Default services.
 */
export const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

type ErrorWithDetail = {
    detail: Array<object>;
};

const errorHasDetailArray = (error: unknown): error is ErrorWithDetail => {
    return (
        error instanceof Object &&
        "detail" in error &&
        error.detail instanceof Array
    );
};

/**
 * Check whether error is a HttpValidationError.
 */
export const isHttpValidationError = (
    error: unknown
): error is HttpValidationError => {
    if (errorHasDetailArray(error)) {
        return "loc" in error.detail[0];
    } else {
        return false;
    }
};

/**
 * Check whether error is a isHttpExceptionModel.
 */
export const isHttpExceptionModel = (
    error: unknown
): error is HttpExceptionModel => {
    if (errorHasDetailArray(error)) {
        return "type" in error.detail[0];
    } else {
        return false;
    }
};

export { useClient, useServices };
